import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "@/contexts/LanguageContext";
import type { Session, User } from "@supabase/supabase-js";

export type AppRole = "admin" | "moderator" | "user";

export type PrimaryInterest = "mental-health" | "education" | "entrepreneurship";

type Profile = {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  preferred_language: string | null;
  preferred_theme: string | null;
  jci_national_org: string | null;
  jci_local_org: string | null;
  primary_interest: PrimaryInterest | null;
  focus_country: string | null;
  onboarding_completed: boolean;
  visited_modules: string[];
  mastery_level: string;
  achievement_unlocked_at: string | null;
  created_at?: string;
  updated_at?: string;
};

type AuthContextValue = {
  loading: boolean;
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  isAdmin: boolean;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const { setTheme } = useTheme();
  const { setLanguage } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // Listener FIRST
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
    });

    // THEN initial session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
      })
      .finally(() => setLoading(false));

    return () => {
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setIsAdmin(false);
      return;
    }

    let cancelled = false;

    // Avoid doing Supabase calls inside onAuthStateChange callback
    setTimeout(() => {
      (async () => {
        const [{ data: profileData }, { data: adminData }] = await Promise.all([
          (supabase as any)
            .from("profiles")
            .select("id, full_name, avatar_url, preferred_language, preferred_theme, jci_national_org, jci_local_org, primary_interest, focus_country, onboarding_completed, visited_modules, mastery_level, achievement_unlocked_at")
            .eq("id", user.id)
            .maybeSingle(),
          supabase.rpc("has_role", { _user_id: user.id, _role: "admin" as any }),
        ]);

        if (cancelled) return;

        const nextProfile = (profileData ?? null) as Profile | null;
        setProfile(nextProfile);
        setIsAdmin(Boolean(adminData));

        // Apply saved preferences immediately
        if (nextProfile?.preferred_theme) setTheme(nextProfile.preferred_theme);
        if (nextProfile?.preferred_language) setLanguage(nextProfile.preferred_language as any, true);
      })().catch(() => {
        if (cancelled) return;
        setProfile(null);
        setIsAdmin(false);
      });
    }, 0);

    return () => {
      cancelled = true;
    };
  }, [user?.id, setLanguage, setTheme]);

  const signInWithMagicLink: AuthContextValue["signInWithMagicLink"] = async (email) => {
    const redirectTo = `${window.location.origin}/auth`;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo },
    });
    return { error: (error as any) ?? null };
  };

  const signInWithGoogle: AuthContextValue["signInWithGoogle"] = async () => {
    const redirectTo = `${window.location.origin}/dashboard`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    return { error: (error as any) ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate("/dashboard", { replace: true });
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      loading,
      session,
      user,
      profile,
      isAdmin,
      signInWithMagicLink,
      signInWithGoogle,
      signOut,
    }),
    [loading, session, user, profile, isAdmin]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
